import { IcebergButton, IconButton } from '@tradesolution/iceberg-ui-react';
import { PickedKjede, PickerMode } from 'components/KjedePicker';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';
import RegionalkjedePicker from 'components/RegionalkjedePicker';
import { useEffect, useState } from 'react';
import { Card, Form, Alert, Collapse } from 'react-bootstrap';
import EnheterBulkApi from 'services/KjederegisteretAdminApi/EnheterBulkApi';
import {
  MeldEnheterInnKjedeCommand,
  KonsoliderKjederCommand,
  KonsoliderKjederResultat,
} from 'services/KjederegisteretAdminApi/EnheterBulkApi/types';
import { GetKjedeByIdResponse } from 'services/KjederegisteretAdminApi/KjedeApi/types';
import HttpErrorHelper from 'utils/HttpErrorHelper';
import useToaster from 'utils/hooks/useToaster';

interface Props {
  kjede: GetKjedeByIdResponse;
}

const Admin = (props: Props) => {
  const toaster = useToaster();

  const [command, setCommand] = useState<MeldEnheterInnKjedeCommand | undefined>({
    flyttFraKjedeNummer: props.kjede.kjedenr,
    flyttTilKjedeNummer: 0,
    fraOgMedDato: new Date(),
    skalOverforeKundenummer: true,
  });

  // Konsolider kjeder state
  const [konsoliderCommand, setKonsoliderCommand] = useState<KonsoliderKjederCommand>({
    kjedeNummer: props.kjede.kjedenr,
    kopierMedlemsnummer: true,
    fraOgMedDato: new Date(),
  });
  const [konsoliderResult, setKonsoliderResult] = useState<KonsoliderKjederResultat | null>(null);
  const [isKonsoliderLoading, setIsKonsoliderLoading] = useState(false);
  const [showKonsoliderResult, setShowKonsoliderResult] = useState(false);

  // pass på å oppdatere fraKjede om en velger en annen kjede
  useEffect(() => {
    setCommand({
      ...command,
      flyttFraKjedeNummer: props.kjede.kjedenr,
      fraOgMedDato: new Date(),
      flyttTilKjedeNummer: 0,
      skalOverforeKundenummer: true,
    });
    setKonsoliderCommand({
      kjedeNummer: props.kjede.kjedenr,
      kopierMedlemsnummer: true,
      fraOgMedDato: new Date(),
    });
    setKonsoliderResult(null);
    setShowKonsoliderResult(false);
  }, [props.kjede.kjedenr]);

  const handleKjedeSelected = (pickedKjeder: PickedKjede[]) => {
    if (pickedKjeder.length > 0) {
      setCommand({ ...command, flyttTilKjedeNummer: parseInt(pickedKjeder[0].kjedenr) });
    } else {
      setCommand({ ...command, flyttTilKjedeNummer: undefined });
    }
  };

  const handleDateSelected = (date: Date | undefined) => {
    if (date) {
      setCommand({ ...command, fraOgMedDato: date });
    }
  };

  const handleKundenrChecked = () => {
    setCommand({ ...command, skalOverforeKundenummer: !command.skalOverforeKundenummer });
  };

  const [isLoading, setIsLoading] = useState(false);

  const canSubmit =
    command?.flyttTilKjedeNummer &&
    command.flyttFraKjedeNummer &&
    command.fraOgMedDato &&
    !isLoading;

  const submit = async () => {
    setIsLoading(true);
    try {
      const result = await EnheterBulkApi.meldEnheterInnKjede(command);

      toaster.success('Suksess', 'Medlemmer flyttet');
    } catch (error) {
      toaster.error('Kunne ikke flytte medlemmer', HttpErrorHelper.formatError(error));
    }
    setIsLoading(false);
  };

  // Konsolider kjeder handlers
  const handleKonsoliderDateSelected = (date: Date | undefined) => {
    if (date) {
      setKonsoliderCommand({ ...konsoliderCommand, fraOgMedDato: date });
    }
  };

  const handleKopierMedlemsnummerChecked = () => {
    setKonsoliderCommand({
      ...konsoliderCommand,
      kopierMedlemsnummer: !konsoliderCommand.kopierMedlemsnummer,
    });
  };

  const canSubmitKonsolider =
    konsoliderCommand.kjedeNummer > 0 && konsoliderCommand.fraOgMedDato && !isKonsoliderLoading;

  const submitKonsolider = async () => {
    setIsKonsoliderLoading(true);
    setKonsoliderResult(null);
    try {
      const result = await EnheterBulkApi.konsoliderKjeder(konsoliderCommand);
      setKonsoliderResult(result);
      setShowKonsoliderResult(true);
      toaster.success(
        'Konsolidering fullført',
        `${result.avsluttedeSubKjeder?.length || 0} underkjeder konsolidert`,
      );
    } catch (error) {
      toaster.error('Kunne ikke konsolidere kjeder', HttpErrorHelper.formatError(error));
    }
    setIsKonsoliderLoading(false);
  };

  return (
    <div>
      <h4>Admin</h4>
      <Card>
        <Card.Body>
          <p>Bruk heller flytt medlemmer bulk funksjon inne på enhetssøket</p>
          <Card.Title style={{ textDecoration: 'line-through' }}>
            Flytt alle medlemmer inn i ny kjede
          </Card.Title>
          <Form>
            <Form.Group>
              <Form.Label>Ny kjede</Form.Label>
              <RegionalkjedePicker
                selected={[command.flyttTilKjedeNummer]}
                pickerMode={PickerMode.Single}
                onSelectionChanged={handleKjedeSelected}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Fra dato</Form.Label>
              <div>
                <NorwegianDatepicker onChange={handleDateSelected} value={command.fraOgMedDato} />
              </div>
            </Form.Group>
            <Form.Group style={{ marginTop: '1rem' }}>
              <Form.Check
                type="checkbox"
                checked={command.skalOverforeKundenummer}
                onChange={handleKundenrChecked}
                label="Avdelingsnr skal følge med"
              />
            </Form.Group>
            <Form.Group style={{ marginTop: '1rem' }}>
              <IcebergButton icon="disk" variant="primary" disabled={!canSubmit} onClick={submit}>
                Lagre
              </IcebergButton>
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>

      <Card style={{ marginTop: '1.5rem' }}>
        <Card.Body>
          <Card.Title>Konsolider underkjeder</Card.Title>
          <p>
            Denne funksjonen flytter alle enheter fra underkjeder opp til denne kjeden (
            {props.kjede.kjedenavn}) og avslutter underkjedene.
          </p>
          <Alert variant="warning">
            <strong>OBS!</strong> Dette er en irreversibel operasjon. Alle enheter i underkjeder vil
            bli flyttet til denne kjeden, og underkjedene vil bli avsluttet.
          </Alert>
          <Form>
            <Form.Group>
              <Form.Label>Kjede som skal konsolideres</Form.Label>
              <Form.Control
                type="text"
                value={`${props.kjede.kjedenavn} (${props.kjede.kjedenr})`}
                disabled
              />
            </Form.Group>
            <Form.Group style={{ marginTop: '1rem' }}>
              <Form.Label>Fra og med dato</Form.Label>
              <div>
                <NorwegianDatepicker
                  onChange={handleKonsoliderDateSelected}
                  value={konsoliderCommand.fraOgMedDato}
                />
              </div>
            </Form.Group>
            <Form.Group style={{ marginTop: '1rem' }}>
              <Form.Check
                type="checkbox"
                checked={konsoliderCommand.kopierMedlemsnummer}
                onChange={handleKopierMedlemsnummerChecked}
                label="Kopier medlemsnummer fra underkjede"
              />
            </Form.Group>
            <Form.Group style={{ marginTop: '1rem' }}>
              <IcebergButton
                icon="synchronize"
                variant="outline-danger"
                disabled={!canSubmitKonsolider}
                onClick={submitKonsolider}
                isLoading={isKonsoliderLoading}
              >
                Konsolider underkjeder
              </IcebergButton>
            </Form.Group>
          </Form>

          <Collapse in={showKonsoliderResult && konsoliderResult !== null}>
            <div style={{ marginTop: '1.5rem' }}>
              <Alert variant="success">
                <Alert.Heading>Konsolidering fullført</Alert.Heading>
                <p>
                  <strong>Antall underkjeder behandlet:</strong>{' '}
                  {konsoliderResult?.avsluttedeSubKjeder?.length || 0}
                </p>
                <p>
                  <strong>Totalt antall enheter oppdatert:</strong>{' '}
                  {konsoliderResult?.updatedEnheter?.length || 0}
                </p>
                <hr />
                {konsoliderResult?.avsluttedeSubKjeder?.map((subKjede) => (
                  <div key={subKjede.kjedeId} style={{ marginBottom: '0.5rem' }}>
                    <strong>{subKjede.kjedeNavn}</strong> ({subKjede.kjedeNummer}):{' '}
                    {subKjede.antallFlyttedeEnheter} enheter flyttet
                    {subKjede.antallSkippet > 0 && (
                      <span className="text-warning">, {subKjede.antallSkippet} hoppet over</span>
                    )}
                  </div>
                ))}
              </Alert>
            </div>
          </Collapse>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Admin;
